import { useResponsiveAddress } from "@solomon-apps/shared/src/components/ResponsiveAddress";
import {
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    InputGroupText
} from "@solomon-labs/design/components/ui";
import React, { useRef } from "react";

import {
    DISABLED_VALUE_CLASS,
    LIGHTBAR_FIELD_MEASURE,
    READ_ONLY_GROUP_CLASS
} from "@/pages/shared/lightbarField";

/**
 * The DS success state for a grouped field. `InputGroup` has no `state` prop
 * and `InputGroupInput` only routes `error` to the group's rail, so the caller
 * adds the rail class — the same `success` rail the DS `ComboboxInput` puts on
 * its own `InputGroup` (`COMBOBOX_STATE_RAIL.success`).
 */
const SUCCESS_GROUP_CLASS =
    "before:bg-status-success-400 after:w-full after:bg-status-success-400";

/**
 * A labeled Solana-address field, label inside, as the design system's Input
 * "Prefix and suffix" recipe: `InputGroup` (the shell draws the lightbar
 * rail) › an inline-start `InputGroupAddon` (no divider, the design
 * system's default since 0.5.0) holding the label as `InputGroupText` ›
 * `InputGroupInput` in the numeric face (APK Narrative). Input is trimmed on
 * change. The label is the group's and the input's `aria-label`.
 *
 * Both the verify (position wallet) and rewards-destination fields are this; it
 * goes read-only as the resting summary when its step collapses. Read-only and
 * disabled both read as inert (muted rail, `foreground-neutral` value) while
 * staying AA; see `pages/shared/lightbarField`.
 *
 * Read-only or disabled, the address shows in full and shortens in the
 * middle only when the field is too narrow (`useResponsiveAddress`); copying
 * it copies the full address. An editable field always holds exactly what
 * the user typed.
 */
export function AddressField({
    label,
    value,
    onChange,
    placeholder,
    readOnly = false,
    disabled = false,
    error = false,
    success = false,
    endAddon,
    onBlur,
    fill = false
}: {
    label: string;
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
    /** Read-only resting state (the collapsed summary). */
    readOnly?: boolean;
    /** Inert state: value shown but the field is not interactive. */
    disabled?: boolean;
    /** Paints the rail red + sets aria-invalid. */
    error?: boolean;
    /** The DS success state: paints the rail `status-success-400`. `error`
     *  wins if both are set. */
    success?: boolean;
    /** Content for an inline-end `InputGroupAddon` (e.g. a `StatusBadge`). */
    endAddon?: React.ReactNode;
    onBlur?: () => void;
    /** Fill the container's width instead of the shared field measure
     *  (`max-w-2xl`): for a field whose frame is already the measure. */
    fill?: boolean;
}): React.ReactElement {
    const inputRef = useRef<HTMLInputElement>(null);
    const inert = readOnly || disabled;
    const fitted = useResponsiveAddress({
        address: value,
        ref: inputRef,
        enabled: inert
    });
    return (
        <InputGroup
            aria-label={label}
            className={`${fill ? "w-full" : LIGHTBAR_FIELD_MEASURE} ${readOnly ? READ_ONLY_GROUP_CLASS : ""} ${success && !error ? SUCCESS_GROUP_CLASS : ""}`}
        >
            <InputGroupAddon align="inline-start">
                <InputGroupText>{label}</InputGroupText>
            </InputGroupAddon>
            <InputGroupInput
                ref={inputRef}
                aria-label={label}
                numeric
                type="text"
                spellCheck={false}
                state={error ? "error" : undefined}
                value={inert ? fitted.text : value}
                title={inert && fitted.text !== value ? value : undefined}
                onCopy={(e) => {
                    if (!inert || fitted.text === value) return;
                    e.clipboardData.setData("text/plain", value);
                    e.preventDefault();
                }}
                onChange={(e) => onChange(e.target.value.trim())}
                onBlur={onBlur}
                readOnly={readOnly}
                disabled={disabled}
                placeholder={placeholder}
                className={DISABLED_VALUE_CLASS}
            />
            {endAddon !== undefined && (
                <InputGroupAddon align="inline-end">{endAddon}</InputGroupAddon>
            )}
        </InputGroup>
    );
}
